import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, InputAdornment, Paper, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { yupResolver } from '@hookform/resolvers/yup';
import { useFieldArray, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { map } from 'lodash';
import TopBar from '../../../components/TopBar';
import CustomTextInput from '../../../components/CustomTextInput';
import { CustomerSelectField } from './CustomerSelectField';
import FormSelectField from '../../../components/form/FormSelectField';
import ProductSelect from '../../products/components/ProductSelect';
import SubscriptionItems from './SubscriptionItems';

const TYPE_OPTIONS = [
  { value: 'prepaid', label: 'Prepaid' },
  { value: 'postpaid', label: 'Postpaid' },
];

const schema = yup.object({
  stripe_customer_id: yup.string().required('Stripe customer ID is required'),
  type: yup.string().oneOf(['prepaid', 'postpaid']).required('Type is required'),
  recurrence_limit: yup
    .number()
    .typeError('Must be a number')
    .positive('Must be positive')
    .integer('Must be a whole number')
    .nullable()
    .transform((v, o) => (o === '' ? null : v)),
  due_after: yup
    .number()
    .typeError('Must be a number')
    .min(0, 'Must be ≥ 0')
    .integer('Must be a whole number')
    .nullable()
    .transform((v, o) => (o === '' ? null : v))
    .required('Due after is required'),
  items: yup
    .array()
    .of(
      yup.object({
        stripe_price_id: yup.string().required('Price is required'),
        pricing_model: yup.string().nullable(),
        meter_code: yup.string().nullable(),
        limit: yup
          .number()
          .typeError('Limit must be a number')
          .integer('Limit must be a whole number')
          .min(0, 'Must be ≥ 0')
          .required('Limit is required')
          .transform((v, o) => (o === '' || o === null || o === undefined ? 0 : v)),
      }),
    )
    .min(1, 'Add at least one price'),
});

export default function SubscriptionForm({
  onSubmit,
  onCancel,
  initialData = {},
  apiErrors = {},
  readOnly = false,
  isLoading = false,
  isEditMode = false,
}) {
  const {
    control,
    handleSubmit,
    setError,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      type: initialData.type || 'prepaid',
      stripe_subscription_id: initialData.stripe_subscription_id || '',
      stripe_customer_id: initialData.stripe_customer_id || '',
      stripe_product_id: initialData.stripe_product_id || '',
      recurrence_limit: initialData.recurrence_limit || null,
      due_after: initialData.due_after,
      items:
        initialData.items?.map((item) => ({
          stripe_price_id: item.stripe_price_id || '',
          pricing_model: item.pricing_model || null,
          meter_code: item.meter_code || null,
          limit: item.limit ?? 0,
        })) || [],
    },
  });
  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => {
    map(apiErrors, (value, key) => setError(key, { message: value, type: 'string' }));
  }, [apiErrors, setError]);
  const selectedProductId = watch('stripe_product_id');
  const previousProductIdRef = useRef(selectedProductId);
  const isCreateMode = !initialData?.stripe_subscription_id && !readOnly;
  const isLimitOnlyEditMode = isEditMode && !readOnly;

  const watchedItems = watch('items', []);

  const { fields: items, replace: replaceItems } = useFieldArray({
    control,
    name: 'items',
  });
  useEffect(() => {
    if (!isCreateMode) {
      return;
    }

    if (previousProductIdRef.current === selectedProductId) return;

    previousProductIdRef.current = selectedProductId;

    if (!selectedProduct) {
      replaceItems([]);
      return;
    }

    let prices = (selectedProduct.prices || []).map((price) => ({
      stripe_price_id: price.stripe_price_id || '',
      pricing_model: price.pricing_model || null,
      meter_code: price.meter_code || null,
      limit: price.pricing_model === 'metered' ? 0 : (price.limit ?? 0),
    }));
    replaceItems(prices);
  }, [isCreateMode, replaceItems, selectedProduct, selectedProductId]);

  const handleFormSubmit = (data) => {
    let payload = {
      ...data,
      stripe_customer_id: data.stripe_customer_id,
      items: data.items
        .map((item) => ({
          stripe_price_id: item.stripe_price_id,
          limit: item.pricing_model === 'metered' ? 0 : Number(item.limit ?? 0),
        }))
        .filter((item) => Boolean(item.stripe_price_id)),
    };

    onSubmit(payload);
  };

  const handleLimitChange = (index, nextLimit) => {
    setValue(`items.${index}.limit`, nextLimit, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const handleItemMetadataChange = (index, price) => {
    const currentItem = watchedItems?.[index];
    const nextPricingModel = price?.pricing_model || null;
    const nextMeterCode = price?.meter_code || null;
    const nextLimit = price?.pricing_model === 'metered' ? 0 : (price?.limit ?? 0);

    if (currentItem?.pricing_model !== nextPricingModel) {
      setValue(`items.${index}.pricing_model`, nextPricingModel, {
        shouldValidate: false,
        shouldDirty: false,
        shouldTouch: false,
      });
    }

    if (currentItem?.meter_code !== nextMeterCode) {
      setValue(`items.${index}.meter_code`, nextMeterCode, {
        shouldValidate: false,
        shouldDirty: false,
        shouldTouch: false,
      });
    }

    if (isCreateMode && Number(currentItem?.limit ?? 0) !== Number(nextLimit)) {
      setValue(`items.${index}.limit`, nextLimit, {
        shouldValidate: true,
        shouldDirty: false,
        shouldTouch: false,
      });
    }
  };

  const handleProductSelect = (product) => {
    setSelectedProduct(product || null);
    if (!isCreateMode) {
      return;
    }

    setValue('type', product?.type || 'prepaid', {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  return (
    <Paper elevation={0}>
      {!readOnly && (
        <Box sx={{ pt: 2, pb: 3 }}>
          <TopBar title="Subscription Form" identifier={initialData.stripe_subscription_id}>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" onClick={onCancel} disabled={isLoading}>
                Cancel
              </Button>
              <Button variant="contained" onClick={handleSubmit(handleFormSubmit)} disabled={isLoading}>
                Save
              </Button>
            </Stack>
          </TopBar>
        </Box>
      )}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 6 }}>
          <CustomerSelectField
            name="stripe_customer_id"
            label="Customer"
            control={control}
            errors={errors}
            readOnly={readOnly || isLimitOnlyEditMode}
          />
        </Grid>
        {isCreateMode && (
          <Grid size={{ xs: 12, sm: 6, md: 6 }}>
            <ProductSelect
              name="stripe_product_id"
              label="Product"
              control={control}
              errors={errors}
              readOnly={readOnly}
              onOptionSelect={handleProductSelect}
            />
          </Grid>
        )}
        <Grid size={{ xs: 12, sm: 4 }}>
          <FormSelectField
            name="type"
            control={control}
            label="Type"
            readOnly={true}
            options={TYPE_OPTIONS}
            errors={errors}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <CustomTextInput
            name="recurrence_limit"
            label="Recurrence Limit"
            type="number"
            placeholder={'∞'}
            control={control}
            errors={errors}
            readOnly={readOnly || isLimitOnlyEditMode}
            slotProps={{
              input: {
                endAdornment: <InputAdornment position="end">Cycles</InputAdornment>,
              },
              inputLabel: {
                shrink: true,
              },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <CustomTextInput
            name="due_after"
            label="Due After"
            type="number"
            control={control}
            errors={errors}
            readOnly={readOnly || isLimitOnlyEditMode}
            adornment="Days"
          />
        </Grid>
        {watchedItems.length > 0 && (
          <Grid size={12}>
            <SubscriptionItems
              items={watchedItems}
              readOnly={readOnly}
              onLimitChange={handleLimitChange}
              onItemMetadataChange={handleItemMetadataChange}
              errors={errors}
            />
            {typeof errors?.items?.message === 'string' && (
              <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                {errors.items.message}
              </Typography>
            )}
          </Grid>
        )}
      </Grid>
    </Paper>
  );
}
